import { useMutation, useQueryClient } from '@tanstack/react-query';

import useUser from '@/hooks/useUser';

import { PORTFOLIO_QUERY_KEY, PORTFOLIOS_QUERY_KEY } from '../usePortfolio';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '../useAggregatePortfolio';
import type { PortfolioQueryClientData, Portfolio } from '../usePortfolio';

import { createManualPortfolioQueryFn } from './createManualPortfolioQueryFn';

interface Props {
  onSuccess: (portfolio: Portfolio) => void;
}

export function useCreateManualPortfolio({ onSuccess }: Props) {
  const queryClient = useQueryClient();
  const { data: user, mutate: mutateUser } = useUser();

  const mutation = useMutation({
    mutationFn: createManualPortfolioQueryFn,
    onSuccess: (portfolio) => {
      queryClient.setQueryData<PortfolioQueryClientData>(
        [PORTFOLIO_QUERY_KEY, portfolio.id],
        () => portfolio
      );

      // Re-enable onboarding in case the user deletes all portfolios
      if (user.portfolio?.hasDismissedDashboardOnboarding === true) {
        mutateUser({
          settings: {
            portfolio: {
              has_dismissed_dashboard_onboarding: false,
            },
          },
        });
      }
      onSuccess(portfolio);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: AGGREGATE_PORTFOLIO_QUERY_KEY,
      });
    },
  });

  return mutation;
}
